import { RefObject, useEffect, useState } from "react";
import { BODY_NO_SCROLL_CLASS } from "src/utils/constants";

/**
 * Reports whether the bar has come to rest at the top of the viewport, by
 * watching a zero-height sentinel sitting where the bar started. The browser
 * does the crossing detection off the main thread — a scroll listener would run
 * on every scroll event just to compare two numbers.
 *
 * `offsetTop` shifts the crossing point: the bar pins once the sentinel is
 * `offsetTop` px above the top of the viewport.
 */
export function useSticky(
  sentinelRef: RefObject<HTMLElement>,
  offsetTop: number
): boolean {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // A drawer or modal locks the body; leave the bar as it was rather than
        // reacting to the layout the lock produces.
        if (document.body.classList.contains(BODY_NO_SCROLL_CLASS)) {
          return;
        }

        // Taken from `isIntersecting` rather than the edges: at very low scroll
        // speeds the crossing frame can report a rect that still overlaps by a
        // fraction of a pixel, and there is no later entry to put it right.
        const rootTop = entry.rootBounds?.top ?? 0;
        const leftAbove = entry.boundingClientRect.top <= rootTop + 1;

        setSticky(!entry.isIntersecting && leftAbove);
      },
      { rootMargin: `${-offsetTop}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [sentinelRef, offsetTop]);

  return sticky;
}
